import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { getSettings, updateSettings } from "../api/settingsApi";
import { useQuery, useMutation } from "@tanstack/react-query";
import LogoUploadModal from "../Models/LogoUploadModal";
import { toast } from "sonner";
import { TabNavigation, TabItem } from "../components/ui/tab-navigation";
import LmsToolRegistration from "./LmsToolRegistration";
import FishboneConfiguration from "./FishboneConfiguration";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const TABS: TabItem[] = [
  { key: "General", label: "General" },
  { key: "AWS Config", label: "AWS Config" },
  { key: "Notifications", label: "Notifications" },
  { key: "Sandbox", label: "Sandbox Settings" },
  { key: "LMS tool registration", label: "LMS tool registration" },
  { key: "Fishbone Configuration", label: "Fishbone Configuration" },
];

interface ThemeSettingsFormValues {
  institutionName: string;
  supportEmail: string;
  primaryColor: string;
  navColor: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  adminAlerts: boolean;
  maintenanceAlert: boolean;
  maintenanceMessage: string;
  sandboxMode: "local" | "cloud";
}

function SettingsForm({
  initialValues,
  validationSchema,
  onSubmit,
  children,
  logoModalOpen,
  setLogoModalOpen,
  logo,
  setLogo,
  showLogoSection = false,
  primaryColor = "#2076D1",
  logoRemoved,
  setLogoRemoved,
  settingsQuery,
}: any) {
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, isSubmitting, handleReset }) => (
        <Form>
          {showLogoSection && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Institute Name
                </label>
                <Field
                  name="institutionName"
                  type="text"
                  className="w-full border rounded px-3 py-2 mb-1"
                  placeholder="Enter your Institute name"
                />
                <ErrorMessage
                  name="institutionName"
                  component="div"
                  className="text-xs text-red-500 mb-2"
                />
                <label className="block text-sm font-medium mb-1 mt-4">
                  Support email
                </label>
                <Field
                  name="supportEmail"
                  type="email"
                  className="w-full border rounded px-3 py-2 mb-1"
                  placeholder="Enter support email"
                />
                <ErrorMessage
                  name="supportEmail"
                  component="div"
                  className="text-xs text-red-500 mb-2"
                />
              </div>
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Institute Logo
                    </label>
                    <div className="text-xs text-gray-500">
                      Must be .PNG or .JPEG, and no larger than 2MB
                    </div>
                  </div>
                  <LogoUploadModal
                    open={logoModalOpen}
                    onClose={() => setLogoModalOpen(false)}
                    onUpload={(file: File | null) => {
                      setLogo(file);
                      setLogoRemoved(false);
                    }}
                    primaryColor={primaryColor}
                  />
                  {(logo ||
                    (settingsQuery.data?.data?.logoUrl && !logoRemoved)) && (
                    <div className="relative inline-block">
                      <div className="w-32 h-32 border rounded-lg flex items-center justify-center bg-gray-50 p-2">
                        <img
                          src={
                            logo
                              ? URL.createObjectURL(logo)
                              : settingsQuery.data?.data?.logoUrl
                          }
                          alt="Institute logo"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-600"
                        onClick={() => {
                          setLogo(null);
                          setLogoRemoved(true);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {!logo &&
                    (!settingsQuery.data?.data?.logoUrl || logoRemoved) && (
                      <button
                        type="button"
                        className={`flex items-center text-[${primaryColor}] text-sm font-medium border border-[${primaryColor}] rounded px-3 py-2 hover:bg-blue-50`}
                        style={{
                          color: primaryColor,
                          borderColor: primaryColor,
                        }}
                        onClick={() => setLogoModalOpen(true)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                        Upload Logo
                      </button>
                    )}
                </div>
              </div>
            </div>
          )}
          {children({ values, setFieldValue, primaryColor })}
          <div className="flex gap-4 mt-8">
            <button
              type="submit"
              style={{ backgroundColor: primaryColor }}
              className="text-white px-6 py-2 rounded font-semibold"
              disabled={isSubmitting}
            >
              Save
            </button>
            <button
              type="button"
              style={{ color: primaryColor, borderColor: primaryColor }}
              className="px-6 py-2 rounded font-semibold border"
              onClick={handleReset}
              disabled={isSubmitting}
            >
              Reset
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

function ThemeSettings() {
  const [activeTab, setActiveTab] = useState<string>("General");
  const [logo, setLogo] = useState<File | null>(null);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const settingsQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getSettings,
    refetchOnWindowFocus: false,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      settingsQuery.refetch();
      toast.success("Settings updated successfully");
    },
    onError: () => {
      toast.error("Failed to update settings");
    },
  });

  const themeConfig = settingsQuery.data?.data;

  const initialValues: ThemeSettingsFormValues = settingsQuery.data
    ? {
        institutionName: themeConfig?.institutionName || "",
        supportEmail: themeConfig?.supportEmail || "",
        primaryColor: themeConfig?.appearance?.primaryColor || "#2E90FA",
        navColor: themeConfig?.appearance?.navigationColor || "#2E90FA",
        emailNotifications: !!themeConfig?.emailNotifications,
        pushNotifications: !!themeConfig?.pushNotifications,
        adminAlerts: !!themeConfig?.adminAlerts,
        maintenanceAlert: !!themeConfig?.maintenanceAlert,
        maintenanceMessage: themeConfig?.maintenanceMessage || "",
        sandboxMode: themeConfig?.sandboxConfig?.mode || "local",
      }
    : {
        institutionName: "",
        supportEmail: "",
        primaryColor: "#2E90FA",
        navColor: "#2E90FA",
        emailNotifications: false,
        pushNotifications: false,
        adminAlerts: false,
        maintenanceAlert: false,
        maintenanceMessage: "",
        sandboxMode: "local",
      };

  const handleSaveSettings = (
    values: ThemeSettingsFormValues,
    { setSubmitting }: any,
  ) => {
    setSubmitting(true);

    const formData = new FormData();
    formData.append("institutionName", values.institutionName);
    formData.append("supportEmail", values.supportEmail);
    if (logo) {
      formData.append("logo", logo);
    }
    if (logoRemoved) {
      formData.append("removeLogo", "true");
    }
    formData.append("maintenanceAlert", String(values.maintenanceAlert));
    formData.append("maintenanceMessage", values.maintenanceMessage);
    formData.append(
      "appearance",
      JSON.stringify({
        primaryColor: values.primaryColor,
        navigationColor: values.navColor,
      }),
    );
    formData.append("emailNotifications", String(values.emailNotifications));
    formData.append("pushNotifications", String(values.pushNotifications));
    formData.append("adminAlerts", String(values.adminAlerts));
    formData.append(
      "sandboxConfig",
      JSON.stringify({
        mode: values.sandboxMode,
      }),
    );

    updateSettingsMutation.mutate(formData as any);
    setSubmitting(false);
  };

  const validationSchema = Yup.object({
    institutionName: Yup.string().required("Institute Name is required"),
    supportEmail: Yup.string()
      .email("Invalid email")
      .required("Support email is required"),
    primaryColor: Yup.string().required("Primary color is required"),
    navColor: Yup.string().required("Navigation color is required"),
    maintenanceAlert: Yup.boolean(),
    maintenanceMessage: Yup.string(),
  });

  // Get the primaryColor from settings API, fallback to default if not loaded
  const primaryColor = themeConfig?.appearance?.primaryColor || "#2076D1";
  return (
    <div className="w-full bg-white p-0 md:p-4">
      <div className="flex items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold my-6 flex-1">Settings</h1>
      </div>

      <div className="mb-8">
        <TabNavigation
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {activeTab === "General" && (
        <SettingsForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSaveSettings}
          logoModalOpen={logoModalOpen}
          setLogoModalOpen={setLogoModalOpen}
          logo={logo}
          setLogo={setLogo}
          showLogoSection={true}
          primaryColor={primaryColor}
          settingsQuery={settingsQuery}
          logoRemoved={logoRemoved}
          setLogoRemoved={setLogoRemoved}
        >
          {({ values, setFieldValue, primaryColor }: any) => (
            <>
              {/* Appearance section */}
              <div className="mb-8">
                <h2 className="font-semibold mb-2">Appearance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Primary Color (Buttons, Tabs etc...)
                    </label>
                    <div className="flex items-center gap-2">
                      <Field
                        name="primaryColor"
                        type="color"
                        className="w-16 h-10 p-0 border rounded"
                      />
                      <Field
                        name="primaryColor"
                        type="text"
                        className="border rounded px-2 py-1 w-32"
                      />
                    </div>
                    <ErrorMessage
                      name="primaryColor"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Navigation Color
                    </label>
                    <div className="flex items-center gap-2">
                      <Field
                        name="navColor"
                        type="color"
                        className="w-16 h-10 p-0 border rounded"
                      />
                      <Field
                        name="navColor"
                        type="text"
                        className="border rounded px-2 py-1 w-32"
                      />
                    </div>
                    <ErrorMessage
                      name="navColor"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>
                </div>
              </div>
              {/* Maintenance alerts section */}
              <div className="mb-8">
                <h2 className="font-semibold mb-2">Maintenance alerts</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Enable maintenance mode
                        </label>
                        <span className="text-gray-600 text-sm">
                          Displays a maintenance message and pauses learner
                          access.
                        </span>
                      </div>
                      <button
                        type="button"
                        id="toggle-switch"
                        aria-pressed={values.maintenanceAlert}
                        onClick={() =>
                          setFieldValue(
                            "maintenanceAlert",
                            !values.maintenanceAlert,
                          )
                        }
                        className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                          values.maintenanceAlert ? "" : "bg-gray-200"
                        }`}
                        style={{
                          minWidth: "44px",
                          backgroundColor: values.maintenanceAlert
                            ? primaryColor
                            : undefined,
                        }}
                        disabled
                      >
                        <span
                          className={`absolute top-1 ${
                            values.maintenanceAlert ? "right-1" : "left-1"
                          } h-4 w-4 rounded-full bg-white shadow transition-all duration-200`}
                        />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Maintenance message
                    </label>
                    <Field
                      as="textarea"
                      name="maintenanceMessage"
                      className="w-full border rounded px-3 py-2 mb-1"
                      placeholder="Enter the message learners will see during maintenance."
                      disabled
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </SettingsForm>
      )}

      {activeTab === "Notifications" && (
        <SettingsForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSaveSettings}
          primaryColor={primaryColor}
        >
          {({ values, setFieldValue, primaryColor }: any) => (
            <>
              <h2 className="text-xl font-semibold mb-6">
                Notification Settings
              </h2>
              <div className="flex flex-col gap-6">
                {/* Email Notifications */}
                <div className="flex flex-row items-center gap-4">
                  <div className="min-w-0 max-w-lg flex-grow">
                    <div className="font-semibold text-base">
                      Email Notifications
                    </div>
                    <div className="text-gray-500 text-sm">
                      Send email notifications to users
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-pressed={values.emailNotifications}
                    onClick={() =>
                      setFieldValue(
                        "emailNotifications",
                        !values.emailNotifications,
                      )
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      values.emailNotifications ? "" : "bg-gray-200"
                    }`}
                    style={{
                      minWidth: "44px",
                      backgroundColor: values.emailNotifications
                        ? primaryColor
                        : undefined,
                    }}
                  >
                    <span
                      className={`absolute top-1 ${
                        values.emailNotifications ? "right-1" : "left-1"
                      } h-4 w-4 rounded-full bg-white shadow transition-all duration-200`}
                    />
                  </button>
                </div>
                {/* Push Notifications */}
                <div className="flex flex-row items-center gap-4">
                  <div className="min-w-0 max-w-lg flex-grow">
                    <div className="font-semibold text-base">
                      Push Notifications
                    </div>
                    <div className="text-gray-500 text-sm">
                      Send push notifications to users via browser
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-pressed={values.pushNotifications}
                    onClick={() =>
                      setFieldValue(
                        "pushNotifications",
                        !values.pushNotifications,
                      )
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      values.pushNotifications ? "" : "bg-gray-200"
                    }`}
                    style={{
                      minWidth: "44px",
                      backgroundColor: values.pushNotifications
                        ? primaryColor
                        : undefined,
                    }}
                  >
                    <span
                      className={`absolute top-1 ${
                        values.pushNotifications ? "right-1" : "left-1"
                      } h-4 w-4 rounded-full bg-white shadow transition-all duration-200`}
                    />
                  </button>
                </div>
                {/* Admin Alerts */}
                <div className="flex flex-row items-center gap-4">
                  <div className="min-w-0 max-w-lg flex-grow">
                    <div className="font-semibold text-base">Admin Alerts</div>
                    <div className="text-gray-500 text-sm">
                      Notifies administrators about important system events.
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-pressed={values.adminAlerts}
                    onClick={() =>
                      setFieldValue("adminAlerts", !values.adminAlerts)
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      values.adminAlerts ? "" : "bg-gray-200"
                    }`}
                    style={{
                      minWidth: "44px",
                      backgroundColor: values.adminAlerts
                        ? primaryColor
                        : undefined,
                    }}
                  >
                    <span
                      className={`absolute top-1 ${
                        values.adminAlerts ? "right-1" : "left-1"
                      } h-4 w-4 rounded-full bg-white shadow transition-all duration-200`}
                    />
                  </button>
                </div>
              </div>
            </>
          )}
        </SettingsForm>
      )}

      {activeTab === "LMS tool registration" && (
        <LmsToolRegistration primaryColor={primaryColor} />
      )}

      {activeTab === "Fishbone Configuration" && (
        <FishboneConfiguration primaryColor={primaryColor} />
      )}

      {activeTab === "Sandbox" && (
        <SettingsForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSaveSettings}
          primaryColor={primaryColor}
        >
          {({ values, setFieldValue, primaryColor }: any) => (
            <>
              <h2 className="text-xl font-semibold mb-6">Sandbox Settings</h2>
              <div className="flex flex-col gap-6">
                <div className="flex flex-row items-center gap-4">
                  <div className="min-w-0 max-w-lg flex-grow">
                    <div className="font-semibold text-base">
                      Global Sandbox Mode
                    </div>
                    <div className="text-gray-500 text-sm">
                      Choose whether sandboxes run locally or in the cloud for
                      all students.
                    </div>
                  </div>
                  <Select
                    value={values.sandboxMode}
                    onValueChange={(value) =>
                      setFieldValue("sandboxMode", value)
                    }
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">Local Mode</SelectItem>
                      <SelectItem value="cloud">Cloud Mode</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </SettingsForm>
      )}

      {activeTab !== "General" &&
        activeTab !== "Notifications" &&
        activeTab !== "LMS tool registration" &&
        activeTab !== "Fishbone Configuration" &&
        activeTab !== "Sandbox" && (
          <div className="text-gray-400 text-center py-16">
            This section is not implemented in demo.
          </div>
        )}
    </div>
  );
}

export default ThemeSettings;
